import React from 'react';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import cn from 'classnames';

import { AdaptiveTableCell, Icons } from '@components/common';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { ITableDatepickerFilter, TTableFilter } from '@interfaces/components/table';
import { IKpiAlertSeverityOutDTO } from '@interfaces/kpi/dto/kpiAlertsDTO';

import { ESeverity, TSeverity } from '@enums/kpi-common';

import styles from '../KpiAlertRulesTable/KpiAlertRulesTable.module.scss';

const wasSentOptions: TSelectOption[] = [
	{
		label: 'Yes',
		value: true,
	}, {
		label: 'No',
		value: false,
	},
];

export function getColumns({
	openNotesModal,
}: {
	openNotesModal: (value: string) => void;
}): ColumnDef<any>[] {
	return [
		{
			header: 'Rule name',
			accessorKey: 'name',
			cell: ({ getValue }: CellContext<any, any>) => {
				const value = getValue();

				return <AdaptiveTableCell text={value} />;
			},
		},
		{
			header: 'Severity',
			accessorKey: 'severity',
			cell: ({ getValue }: CellContext<any, any>) => {
				const value = getValue();

				return (
					<div className={styles.severityCell}>
						<div
							className={cn(styles.colorLabel, {
								[styles.warning]: value.name === ESeverity.warning,
								[styles.error]: value.name === ESeverity.error,
								[styles.critical]: value.name === ESeverity.critical,
								[styles.informational]: value.name === ESeverity.informational,
							})}
						/>
						<AdaptiveTableCell text={value.title} />
					</div>
				);
			},
		},
		{
			header: 'Fire time',
			accessorKey: 'happenedAtText',
			cell: ({ getValue }: CellContext<any, any>) => {
				const value = getValue();

				return <AdaptiveTableCell text={value} />;
			},

		},
		{
			header: 'Was sent',
			accessorKey: 'isSent',
			cell: ({ getValue }: CellContext<any, any>) => {
				const value = getValue();

				return <AdaptiveTableCell text={value ? 'Yes' : 'No'} />;
			},
		},
		{
			header: 'Description',
			accessorKey: 'messageBody',
			enableSorting: false,
			cell: ({ getValue }: CellContext<any, any>) => {
				const value = getValue();
				const onNotesClick = () => openNotesModal(value);

				return (
					<div className={styles.notesColumn}>
						{value && <div
							className={styles.notesIcon}
							onClick={onNotesClick}
						>
							{Icons.summaryIcon()}
						</div>
						}
						<div className={styles.notes}>
							{value}
						</div>
					</div>
				);
			},
		},
	];
}

export function getFilters({
	severity,
	handleSetSelectedSeverity,
	wasSent,
	handleSetWasSent,
	severityOptions = [],
}: {
	severity?: string | null;
	handleSetSelectedSeverity: (value: TSelectOption<TSeverity> | null) => void;
	wasSent?: TSelectOption | null;
	handleSetWasSent: (value: TSelectOption<boolean> | null) => void;
	severityOptions: TSelectOption<TSeverity, IKpiAlertSeverityOutDTO>[];
}): TTableFilter[] {
	const noColor = { color: null, hoverColor: null };

	return [
		{
			name: 'severity',
			placeholder: 'Severity',
			option: severity ? { ...severityOptions?.find((item) => item?.value === severity), ...noColor } : null,
			options: severityOptions.map((item) => ({ ...item, ...noColor })),
			minWidth: '200px',
			setState: handleSetSelectedSeverity,
			defaultValue: 'All',
		},
		{
			name: 'isSent',
			placeholder: 'Was Sent',
			option: wasSent,
			options: wasSentOptions,
			minWidth: '200px',
			setState: handleSetWasSent,
			defaultValue: 'All',
		},
	];
}

export function getDateFilters({
	fireDate,
	onChangeFireDate,
}: {
	fireDate: Date | null;
	onChangeFireDate: (value: number | null) => void;
}): Array<ITableDatepickerFilter> {
	return [
		{
			name: 'from',
			label: 'Fire Time',
			handleChange: onChangeFireDate,
			selected: fireDate,
			disableFutureDates: true,
			maxDate: new Date(),
		},
	];
}
